import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Camera, Loader2, Share2, Copy, ArrowRight, Users, LogOut } from "lucide-react";
import { toast } from "sonner";
import { getFridge, joinFridge } from "@/lib/api";
import { getDeviceId, getDisplayName, setDisplayName } from "@/lib/device";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export default function GroupFridge() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [name, setName] = useState(getDisplayName());
  const [joining, setJoining] = useState(false);

  const deviceId = getDeviceId();

  const load = useCallback(() => {
    return getFridge(code)
      .then(setData)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [code]);

  useEffect(() => {
    load();
  }, [load]);

  const isMember = useMemo(
    () => !!data && (data.fridge.members || []).some((m) => m.device_id === deviceId),
    [data, deviceId]
  );

  const shareLink = `${window.location.origin}/fridge/${code}`;

  const join = async () => {
    if (!name.trim()) return toast("Add your name to join.");
    setJoining(true);
    try {
      setDisplayName(name.trim());
      await joinFridge(code, name.trim());
      await load();
      toast("You're in. Watch the milk.");
    } catch {
      toast("Couldn't join. Try again.");
    } finally {
      setJoining(false);
    }
  };

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: "Our shared fridge", text: `Join our fridge investigation. Code: ${code}`, url: shareLink });
        return;
      }
    } catch (e) {
      if (e && e.name === "AbortError") return;
    }
    navigator.clipboard?.writeText(shareLink);
    toast("Link copied. Send it round.");
  };

  if (loading) {
    return (
      <div className="app-shell min-h-[100dvh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 spin-slow text-[#4a5c62]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="app-shell min-h-[100dvh] flex flex-col items-center justify-center px-8 text-center gap-4">
        <p className="font-serif text-3xl">That fridge doesn't exist.</p>
        <button onClick={() => navigate("/group")} className="btn-ink rounded-sm px-6 py-3">Open another</button>
      </div>
    );
  }

  const fridge = data.fridge;

  // Join gate for non-members (e.g. arriving via shared link)
  if (!isMember) {
    return (
      <div className="app-shell min-h-[100dvh] px-6 pt-[calc(env(safe-area-inset-top)+1.5rem)] pb-[calc(env(safe-area-inset-bottom)+3rem)] flex flex-col" data-testid="group-join-gate">
        <button onClick={() => navigate("/")} className="w-9 h-9 rounded-full glass flex items-center justify-center">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <div className="flex-1 flex flex-col justify-center">
          <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#4a5c62]">You've been invited</p>
          <h1 className="font-serif text-4xl font-black tracking-tighter mt-3 leading-[0.95]">{fridge.name}</h1>
          <p className="text-base text-[#4a5c62] mt-3">
            {(fridge.members || []).length} {(fridge.members || []).length === 1 ? "person is" : "people are"} already watching this fridge.
          </p>
          <label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4a5c62] mt-8">Your name</label>
          <input
            data-testid="join-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Dave"
            maxLength={24}
            className="w-full glass rounded-sm px-4 py-3 mt-2 outline-none focus:ring-2 focus:ring-[#16242a] placeholder:text-[#7c8f95]"
          />
          <button data-testid="join-confirm-btn" onClick={join} disabled={joining} className="btn-ink w-full rounded-sm py-4 mt-5 flex items-center justify-center gap-2 disabled:opacity-60">
            {joining ? <Loader2 className="w-5 h-5 spin-slow" /> : <Users className="w-5 h-5" strokeWidth={1.5} />}
            Join this fridge
          </button>
        </div>
      </div>
    );
  }

  const comps = data.comparisons || [];

  return (
    <div className="app-shell min-h-[100dvh] px-6 pt-[calc(env(safe-area-inset-top)+1.5rem)] pb-[calc(env(safe-area-inset-bottom)+3rem)]" data-testid="group-fridge-screen">
      <div className="flex items-center justify-between mb-6">
        <button data-testid="fridge-back-btn" onClick={() => navigate("/")} className="w-9 h-9 rounded-full glass flex items-center justify-center">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <span className="text-xs uppercase tracking-[0.28em] font-semibold text-[#4a5c62]">Shared fridge</span>
        <button
          data-testid="fridge-leave-btn"
          onClick={() => navigate("/")}
          className="w-9 h-9 rounded-full glass flex items-center justify-center"
          title="Leave"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      {/* Fridge header / code */}
      <motion.div className="shelf rounded-sm p-5" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-serif text-3xl font-black tracking-tight leading-none">{fridge.name}</h1>
        <div className="flex items-center gap-3 mt-3">
          <span className="font-mono text-lg tracking-[0.3em] bg-[#16242a] text-[#eef5f6] px-3 py-1 rounded-sm" data-testid="fridge-code">{fridge.code}</span>
          <button data-testid="fridge-share-btn" onClick={share} className="flex items-center gap-2 text-sm font-medium text-[#16242a]">
            <Share2 className="w-4 h-4" strokeWidth={1.5} /> Share
          </button>
          <button onClick={() => { navigator.clipboard?.writeText(shareLink); toast("Link copied."); }} className="flex items-center gap-1 text-sm text-[#4a5c62]">
            <Copy className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {(fridge.members || []).map((m) => (
            <span key={m.device_id} className="text-xs bg-white/60 hairline rounded-full px-3 py-1.5 text-[#16242a]">{m.name}</span>
          ))}
        </div>
      </motion.div>

      <button
        data-testid="fridge-add-snap-btn"
        onClick={() => navigate(`/capture?role=before&fridge=${code}`)}
        className="btn-ink w-full rounded-sm py-4 mt-5 flex items-center justify-center gap-2"
      >
        <Camera className="w-5 h-5" strokeWidth={1.5} /> Add a snap to this fridge
      </button>

      {data.longest_resident && (
        <div className="glass rounded-sm p-4 mt-5" data-testid="fridge-longest-resident">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4a5c62]">Longest resident</p>
          <p className="font-serif text-2xl mt-1">{data.longest_resident.name}</p>
          <p className="text-sm text-[#4a5c62] mt-0.5">
            Survived {data.longest_resident.count} raid{data.longest_resident.count > 1 ? "s" : ""}. Nobody claims it.
          </p>
        </div>
      )}

      <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#4a5c62] mt-8 mb-4">The raids</p>
      {comps.length === 0 ? (
        <div className="glass rounded-sm p-6 text-center">
          <p className="font-serif text-2xl leading-tight">No raids logged yet.</p>
          <p className="text-sm text-[#4a5c62] mt-2">Be the first to catch someone in the act.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {comps.map((c, i) => (
            <motion.button
              key={c.id}
              data-testid={`fridge-comp-${c.id}`}
              onClick={() => navigate(`/reveal/${c.id}`)}
              className="glass rounded-sm p-3 flex items-center gap-4 text-left"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
            >
              <div className="flex gap-1 shrink-0">
                <img src={`${BACKEND_URL}${c.before_url}`} alt="before" loading="lazy" className="w-11 h-14 object-cover rounded-sm border border-[#c4d2d5] bg-[#dbe6e9]" />
                <img src={`${BACKEND_URL}${c.after_url}`} alt="after" loading="lazy" className="w-11 h-14 object-cover rounded-sm border border-[#c4d2d5] bg-[#dbe6e9]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-[0.16em] text-[#4a5c62]">
                  {c.member_name ? `${c.member_name} · ` : ""}{timeAgo(c.created_at)}
                </p>
                <p className="font-serif text-lg leading-tight mt-0.5 truncate">{c.result?.summary_stat || "A quiet raid"}</p>
                <p className="text-sm text-[#4a5c62] mt-0.5 line-clamp-1">{c.result?.summary_copy || c.result?.vibe_copy}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-[#4a5c62] shrink-0" strokeWidth={1.5} />
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
}
